<template>
    <Modal ref="modal" title="Create Venue" size="large">
        <FormField label="Venue name" class="tw-mb-3">
            <Textbox v-model="name" placeholder="Venue name" maxlength="255" />
        </FormField>
        <Address v-model="address" :locales="locales" />
        <Banner v-if="errorMessage" type="is-danger" class="tw-mt-3">
            {{ errorMessage }}
        </Banner>

        <template #footer>
            <div class="tw-flex tw-justify-end tw-gap-2">
                <Button :disabled="isSaving" @click="modal?.close()">Cancel</Button>
                <Button type="is-success" :disabled="!isValid || isSaving" :loading="isSaving" @click="createVenue">
                    Create and select venue
                </Button>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import Address from '@/assets/js/eventbooks/components/forms/address.vue';
import Banner from '@/js/components/Layout/Banner.vue';
import Button from '@/js/components/controls/Button.vue';
import Modal from '@/js/components/Modal.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import type { Address as AddressFields } from '@/js/types/address.ts';

type VenueOption = Record<string, unknown> &
    Pick<AddressFields, 'id' | 'name' | 'address1' | 'address2' | 'city' | 'postcode' | 'country' | 'address'> & {
        name: string;
    };

type VenueAddress = Pick<AddressFields, 'address1' | 'address2' | 'city' | 'postcode' | 'country'>;

const props = defineProps<{
    accountId: number;
    locales: string[];
}>();

const emit = defineEmits<{
    created: [address: VenueOption];
}>();

const modal = useTemplateRef('modal');
const name = ref('');
const address = ref<VenueAddress>(createEmptyAddress());
const isSaving = ref(false);
const errorMessage = ref('');
const isValid = computed(() =>
    Boolean(
        name.value.trim() &&
        address.value.address1.trim() &&
        address.value.city.trim() &&
        address.value.postcode.trim() &&
        address.value.country.trim()
    )
);

function createEmptyAddress(): VenueAddress {
    return {
        address1: '',
        address2: '',
        city: '',
        postcode: '',
        country: 'United Kingdom',
    };
}

const open = (initialName = ''): void => {
    name.value = initialName;
    address.value = createEmptyAddress();
    errorMessage.value = '';
    modal.value?.open();
};

const createVenue = async (): Promise<void> => {
    if (!isValid.value || isSaving.value) {
        return;
    }

    isSaving.value = true;
    errorMessage.value = '';

    try {
        const { data } = await axios.post<VenueOption>(
            route('api.account.addresses.create', { account: props.accountId }),
            {
                name: name.value.trim(),
                ...address.value,
            }
        );

        emit('created', data);
        modal.value?.close();
    } catch (error: unknown) {
        const responseData = axios.isAxiosError(error)
            ? (error.response?.data as { errors?: Record<string, string[]>; message?: string } | undefined)
            : undefined;
        errorMessage.value = responseData?.errors
            ? Object.values(responseData.errors).flat().join(' ')
            : (responseData?.message ?? 'Unable to create this venue. Please try again.');
    } finally {
        isSaving.value = false;
    }
};

defineExpose({ open });
</script>
